// MARK:- Custom: keep the shipping insurance line item present in the cart
//
// Owns the *presence* of the insurance line item in the cart. Pricing is owned by a
// Shopify Cart Transform Function installed via the merchant's app — that function reads
// the same shop metafield (`shipping_insurance.product_variant`) we read here, plus a
// rate, and updates the line item's price every time the cart changes.
//
// Cart Transform Functions can only update existing lines, not insert new ones. So this
// script is what actually puts the insurance line into the cart in the first place, and
// removes it when the cart goes empty.
//
// Behavior:
// 1. On DOMContentLoaded, if the cart has any non-insurance items but no insurance line,
//    add the insurance variant (qty 1).
// 2. Subscribes to PUB_SUB_EVENTS.cartItemAdd — same check after every add-to-cart.
// 3. Subscribes to PUB_SUB_EVENTS.cartUpdate — if the cart now contains *only* the
//    insurance line, remove it so the cart shows as empty.
// 4. Uses an in-flight lock to prevent double-fires from rapid clicks.
// 5. No-ops if the shop metafield is unset (config.variantId is null).

(function () {
  const configEl = document.getElementById("ShippingInsuranceConfig");
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn("[shipping-insurance] failed to parse config blob", e);
    return;
  }

  const productId = config.productId;
  const variantId = config.variantId;
  // MARK:- Server-rendered initial state from Liquid (avoids a /cart.js fetch on page load)
  const initialOptedOut = config.optedOut === true;
  const initialHasInsuranceLine = config.hasInsuranceLine === true;
  const initialHasRealItems = config.hasRealItems === true;
  if (!productId || !variantId) return;

  let inFlight = false;

  function fetchCart() {
    return fetch(`${window.routes?.cart_url || "/cart"}.js`, {
      headers: { Accept: "application/json" },
    }).then((r) => r.json());
  }

  function hasRealItems(cart) {
    return cart.items.some((item) => item.product_id !== productId);
  }

  function findInsuranceLine(cart) {
    // Returns 1-based line index, or 0 if not found.
    for (let i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === productId) return i + 1;
    }
    return 0;
  }

  function isOnlyInsurance(cart) {
    return cart.items.length > 0 && !hasRealItems(cart);
  }

  function refreshCartUI() {
    // The existing CartItems / MiniCart custom elements expose updateSectionContents()
    // which re-fetches the cart-related sections via Shopify's section render API and
    // swaps the inner HTML. Calling these picks up our new insurance line in both views.
    try {
      window.miniCart?.updateSectionContents?.();
    } catch (e) {
      // ignore
    }
    try {
      document.querySelector("cart-items")?.updateSectionContents?.();
    } catch (e) {
      // ignore
    }
  }

  function addInsurance() {
    if (inFlight) return Promise.resolve();
    inFlight = true;
    return fetch(`${window.routes?.cart_add_url || "/cart/add"}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((r) => r.json())
      .catch((e) => {
        console.warn("[shipping-insurance] add failed", e);
      })
      .finally(() => {
        inFlight = false;
      });
  }

  function removeInsurance(lineIndex) {
    if (inFlight) return Promise.resolve();
    inFlight = true;
    return fetch(`${window.routes?.cart_change_url || "/cart/change"}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ line: lineIndex, quantity: 0 }),
    })
      .then((r) => r.json())
      .catch((e) => {
        console.warn("[shipping-insurance] remove failed", e);
      })
      .finally(() => {
        inFlight = false;
      });
  }

  // MARK:- Clear the opt-out cart attribute (fire-and-forget metadata write)
  function clearOptOut() {
    return fetch(`${window.routes?.cart_update_url || "/cart/update"}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { _shipping_insurance_opted_out: "" } }),
    }).catch((e) => console.warn("[shipping-insurance] clearOptOut failed", e));
  }

  function ensurePresence() {
    return fetchCart().then((cart) => {
      const insuranceLine = findInsuranceLine(cart);
      const realItems = hasRealItems(cart);
      // MARK:- Always re-read live cart attributes here — Liquid initialOptedOut is only valid at init
      const optedOut = cart.attributes?._shipping_insurance_opted_out === "true";

      // Auto-remove: cart contains ONLY the insurance line → strip it. (Runs even when opted out.)
      if (insuranceLine && !realItems) {
        return removeInsurance(insuranceLine).then(refreshCartUI);
      }

      // Auto-add: cart has real items but no insurance line. Honor shopper opt-out from checkout.
      if (!insuranceLine && realItems) {
        if (optedOut) return null;
        return addInsurance().then(refreshCartUI);
      }

      return null;
    });
  }

  function init() {
    // MARK:- Page-load short-circuit using Liquid-rendered cart state — zero fetches in steady state
    if (initialHasInsuranceLine && !initialHasRealItems) {
      fetchCart().then((cart) => {
        const lineIndex = findInsuranceLine(cart);
        if (lineIndex) removeInsurance(lineIndex).then(refreshCartUI);
      });
    } else if (!initialHasInsuranceLine && initialHasRealItems && !initialOptedOut) {
      addInsurance().then(refreshCartUI);
    }

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartItemAdd, (event) => {
        // Skip the event we triggered ourselves to avoid reentry.
        if (event && event.source === "shipping-insurance") return;

        // Skip if the user-added items array is just the insurance variant
        // (e.g. someone deep-linked /cart/add?id=INSURANCE_VARIANT).
        const items = event?.items || [];
        const onlyInsurance =
          items.length > 0 &&
          items.every((it) => it && (it.product_id === productId || it.id === variantId));
        if (onlyInsurance) return;

        ensurePresence();
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === "shipping-insurance") return;
        const cart = event?.cartData;
        if (!cart) return;
        if (isOnlyInsurance(cart)) {
          const lineIndex = findInsuranceLine(cart);
          if (lineIndex) removeInsurance(lineIndex).then(refreshCartUI);
          return;
        }

        // MARK:- Cart fully empty → clear sticky opt-out so next session starts fresh.
        if (
          cart.item_count === 0 &&
          cart.attributes?._shipping_insurance_opted_out === "true"
        ) {
          clearOptOut();
        }
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
